/**
 * Invalidation map — plan 23.
 *
 * Every mutation in the app declares its invalidation intent by name in
 * this file rather than sprinkling `qc.invalidateQueries(...)` calls at
 * random call sites. The benefits:
 *
 *   1. A refactor that reshapes a query key updates invalidations in one
 *      place instead of hunting across features.
 *   2. Documentation: the shape of this object is the authoritative map
 *      of which mutations affect which caches.
 *   3. Mutation hooks become skinny: `onSuccess: () => invalidate(qc, 'createBooking')`.
 *
 * Rule: do not call `qc.invalidateQueries` in a mutation hook's `onSuccess`
 * directly. Always name the mutation in `INVALIDATIONS` and call `invalidate`.
 */

import type { QueryClient, QueryKey } from '@tanstack/react-query';
import {
  adminKeys,
  authKeys,
  bookingKeys,
  couponKeys,
  profileKeys,
  purchaseKeys,
  testimonialKeys,
} from './queryKeys';

/**
 * Canonical set of mutation names. Adding a new mutation? Declare it here
 * first; TypeScript will surface every consumer that needs updating.
 */
export type MutationName =
  // auth
  | 'login'
  | 'register'
  | 'logout'
  // booking
  | 'createBooking'
  | 'freeCheckout'
  | 'capturePayment'
  | 'completePurchase'
  | 'submitQuestionnaire'
  // profile
  | 'updateProfile'
  | 'createBirthDataRequest'
  | 'updateMarketingConsent'
  // testimonials
  | 'submitTestimonial'
  | 'updateTestimonialStatus'
  | 'deleteTestimonial'
  // admin - users
  | 'updateAdminUser'
  | 'addUserNote'
  // admin - birth data requests
  | 'approveBirthRequest'
  | 'rejectBirthRequest'
  // admin - coupons
  | 'createCoupon'
  | 'updateCoupon'
  | 'deleteCoupon'
  // admin - email templates
  | 'updateEmailTemplate'
  // admin - sessions
  | 'cancelSession'
  | 'rescheduleSession'
  | 'updateSessionRecording'
  | 'notifyClientSession'
  // admin - client detail (plan 70)
  | 'updateAdminClientContacts'
  | 'createBookingResource'
  | 'updateBookingResource'
  | 'deleteBookingResource'
  // admin - contact inbox (plan 78)
  | 'updateContactThread'
  | 'sendContactReply'
  | 'addContactNote'
  // admin - contact inbox delete (plan 88)
  | 'deleteContactThread'
  // admin - audio report builder (plan 208)
  | 'createAudioReportOrder'
  | 'updateAudioReportOrder'
  | 'generateAudioReport'
  | 'approveAudioReport'
  | 'sendAudioReportDeliveryEmail'
  | 'regenerateAudioReport'
  | 'resendAudioReportDeliveryEmail'
  | 'cancelAudioReportOrder';

const INVALIDATIONS: Record<MutationName, ReadonlyArray<QueryKey>> = {
  // Auth
  login: [authKeys.me()],
  register: [authKeys.me()],
  logout: [authKeys.me(), bookingKeys.mine, purchaseKeys.mine, profileKeys.mine()],

  // Booking — a successful booking affects the user's bookings + purchases.
  createBooking: [bookingKeys.mine, purchaseKeys.mine, bookingKeys.unavailable()],
  freeCheckout: [purchaseKeys.mine, bookingKeys.mine, bookingKeys.unavailable()],
  capturePayment: [purchaseKeys.mine, bookingKeys.mine, bookingKeys.unavailable()],
  completePurchase: [purchaseKeys.mine, bookingKeys.mine],
  // A submitted questionnaire flips the purchase's `step` server-side
  // (`schedule` → `completed`, or onward to the booking step), surfaces a
  // new Birth Data Request when birth-data was provided, and clears the
  // dashboard's "Complete Details" pill. Invalidate purchases + bookings
  // so the dashboard renders the new state without a hard reload.
  submitQuestionnaire: [purchaseKeys.mine, bookingKeys.mine, profileKeys.mine()],

  // Profile — the dashboard profile read AND ['me'] are both derived from
  // the user row on the server, so both caches invalidate together.
  updateProfile: [profileKeys.mine(), authKeys.me()],
  createBirthDataRequest: [profileKeys.mine(), adminKeys.birthDataRequests()],
  // Plan 74 — marketing consent surfaces in `/api/me` + `/api/user/profile`.
  updateMarketingConsent: [profileKeys.mine(), authKeys.me()],

  // Testimonials
  submitTestimonial: [testimonialKeys.public(), testimonialKeys.admin()],
  updateTestimonialStatus: [testimonialKeys.admin(), testimonialKeys.public()],
  deleteTestimonial: [testimonialKeys.admin(), testimonialKeys.public()],

  // Admin user edits
  updateAdminUser: [adminKeys.users(), profileKeys.mine()],
  addUserNote: [], // Scoped below — needs dynamic user id.

  // Birth-data requests
  approveBirthRequest: [adminKeys.birthDataRequests(), adminKeys.users(), profileKeys.mine()],
  rejectBirthRequest: [adminKeys.birthDataRequests()],

  // Coupons
  createCoupon: [couponKeys.adminList()],
  updateCoupon: [couponKeys.adminList()],
  deleteCoupon: [couponKeys.adminList()],

  // Email templates
  updateEmailTemplate: [adminKeys.emailSettings()],

  // Sessions
  cancelSession: [adminKeys.sessions(), adminKeys.activities()],
  rescheduleSession: [adminKeys.sessions(), adminKeys.activities()],
  updateSessionRecording: [adminKeys.sessions()],
  // Plan 85 — notify-client passes the dynamic per-client sessions key
  // through `extra` (the hook does this) so the static set is just the
  // global activities-feed fan-out.
  notifyClientSession: [adminKeys.activities()],

  // Client detail (plan 70). Each mutation passes the dynamic per-client key
  // through `extra` (the hooks below do this) so the static set here only
  // includes the global fan-out (activities feed picks up the new entry).
  updateAdminClientContacts: [adminKeys.activities()],
  createBookingResource: [],
  updateBookingResource: [],
  deleteBookingResource: [],

  // Contact inbox (plan 78). Each mutation invalidates the inbox tree —
  // list keys vary by filter, but `adminKeys.contactInbox.all` is the
  // shared prefix React Query uses to invalidate every list cache and
  // every per-thread detail cache in one shot. Reply also bumps the
  // global activity feed because the server logs a `Contact Reply Sent`
  // entry. Per-thread detail keys are passed via `extra` from the hooks
  // so a single mutation refreshes the visible thread immediately.
  updateContactThread: [adminKeys.contactInbox.all],
  sendContactReply: [adminKeys.contactInbox.all, adminKeys.activities()],
  addContactNote: [adminKeys.contactInbox.all],

  // Plan 88 — delete invalidates the whole inbox tree (the list refetches
  // without the removed row) and bumps the activities feed (the server
  // logs `admin.contact_thread.delete`). The per-thread detail cache is
  // removed (not invalidated) inside the hook so the now-gone thread
  // can't refetch a 404.
  deleteContactThread: [adminKeys.contactInbox.all, adminKeys.activities()],

  // Plan 208 — Audio Report Builder mutations. Every mutation in the
  // builder invalidates the entire `audioReportBuilder` subtree (list
  // caches vary by filter; the detail+renderStatus per-id keys live
  // under the same shared prefix) and bumps the global activity feed
  // (the server logs `admin.audio_report.*` rows on every transition).
  // Per-detail keys are passed via `extra` from the hooks so the open
  // detail page refreshes immediately.
  createAudioReportOrder: [adminKeys.audioReportBuilder.all, adminKeys.activities()],
  updateAudioReportOrder: [adminKeys.audioReportBuilder.all, adminKeys.activities()],
  generateAudioReport: [adminKeys.audioReportBuilder.all, adminKeys.activities()],
  approveAudioReport: [adminKeys.audioReportBuilder.all, adminKeys.activities()],
  sendAudioReportDeliveryEmail: [adminKeys.audioReportBuilder.all, adminKeys.activities()],
  regenerateAudioReport: [adminKeys.audioReportBuilder.all, adminKeys.activities()],
  resendAudioReportDeliveryEmail: [adminKeys.audioReportBuilder.all, adminKeys.activities()],
  cancelAudioReportOrder: [adminKeys.audioReportBuilder.all, adminKeys.activities()],
};

/**
 * Invalidate every query key registered for `name`. A mutation's
 * `onSuccess` looks like:
 *
 *   onSuccess: () => invalidate(qc, 'createBooking')
 *
 * For mutations whose invalidation set depends on runtime data (e.g.
 * `addUserNote` which affects `adminKeys.userNotes(userId)`), pass the
 * extra keys via `extra`.
 */
export function invalidate(
  qc: QueryClient,
  name: MutationName,
  extra: ReadonlyArray<QueryKey> = []
): void {
  const registered = INVALIDATIONS[name] ?? [];
  for (const key of [...registered, ...extra]) {
    qc.invalidateQueries({ queryKey: key });
  }
}

/**
 * Expose the map read-only for tests. Do NOT mutate at runtime.
 */
export function getInvalidationKeys(name: MutationName): ReadonlyArray<QueryKey> {
  return INVALIDATIONS[name] ?? [];
}

/**
 * All mutation names for tests / exhaustiveness checks.
 */
export const MUTATION_NAMES: ReadonlyArray<MutationName> = Object.keys(
  INVALIDATIONS
) as MutationName[];
